import React from 'react';
import {Control, Controller, FieldErrors, FieldValues, Path} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import InputWithCharacterCounter from '../../../components/slides/highlighted/common/InputWithCharacterCounter';
import {type TypedTranslation} from '../../../i18n';

interface IReactHookFormInputFieldWithCounterProps<T extends FieldValues> {
    text: string;
    textClassName?: string;
    fieldName: Path<T>;
    errors: FieldErrors<T>;
    control: Control<T>;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    className?: string;
    maxLength: number;
    onLimitChange?: (isOverLimit: boolean) => void;
    disabled?: boolean;
    dataCy?: string;
}

function ReactHookFormInputFieldWithCounter<T extends FieldValues>({
    text,
    fieldName,
    control,
    errors,
    onKeyUp,
    disabled,
    onLimitChange,
    maxLength,
    className,
    textClassName,
    dataCy,
}: IReactHookFormInputFieldWithCounterProps<T>): JSX.Element {
    const {t} = useTranslation();

    return (
        <div className="u-flex u-flex-col" style={{width: '100%'}}>
            <div className={`${textClassName || 'is-text'}`}>{text}</div>
            <Controller
                name={fieldName}
                control={control}
                render={({field}) => (
                    <InputWithCharacterCounter
                        maxLength={maxLength}
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder={text}
                        className={className}
                        onKeyUp={onKeyUp}
                        disabled={disabled}
                        onLimitChange={onLimitChange}
                        dataCy={dataCy}
                    />
                )}
            />
            <div className="error-msg u-flex u-h-right">
                {errors[fieldName] && <span>{t(errors[fieldName]?.message as TypedTranslation)}</span>}
            </div>
        </div>
    );
}

export default ReactHookFormInputFieldWithCounter;
